import { useQuery } from '@tanstack/react-query';
import { Bell, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

export const NotificationsWidget = () => {
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications-widget'],
    queryFn: async () => {
      const { data: notifications } = await supabase
        .from('notifications')
        .select('id, type, read, created_at')
        .order('created_at', { ascending: false })
        .limit(100);

      const unread = notifications?.filter(n => !n.read).length || 0;
      const read = notifications?.filter(n => n.read).length || 0;

      // Get notifications by type
      const emergencyNotifications = notifications?.filter(n => 
        n.type.includes('EMERGENCY') || n.type.includes('ALERT')
      ).length || 0;

      const shiftNotifications = notifications?.filter(n => 
        n.type.includes('SHIFT')
      ).length || 0;

      const leaveNotifications = notifications?.filter(n => 
        n.type.includes('LEAVE')
      ).length || 0;

      // Get notifications from last 24 hours
      const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const recentNotifications = notifications?.filter(n => 
        new Date(n.created_at) > last24h
      ).length || 0;

      return {
        total: notifications?.length || 0,
        unread,
        read,
        emergency: emergencyNotifications,
        shift: shiftNotifications,
        leave: leaveNotifications,
        recent: recentNotifications
      };
    },
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-32 bg-muted rounded"></div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <Bell className="w-6 h-6 text-amber-500" />
        <h3 className="text-lg font-semibold">Notifications</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{notificationsData?.unread || 0}</div>
          <div className="text-sm text-muted-foreground">Unread</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{notificationsData?.recent || 0}</div>
          <div className="text-sm text-muted-foreground">Last 24h</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm">Emergency</span>
          </div>
          <Badge variant={notificationsData?.emergency ? "destructive" : "secondary"}>
            {notificationsData?.emergency || 0}
          </Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="text-sm">Shift Related</span>
          </div>
          <Badge variant="default">{notificationsData?.shift || 0}</Badge>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm">Leave Related</span>
          </div>
          <Badge variant="outline">{notificationsData?.leave || 0}</Badge>
        </div>
      </div>
    </Card>
  );
};